import { assert, assertNever } from '~/util';
import { defaultSampleAt } from '../waveform';
import { constraintCache } from './constraints';
import type { WaveformApi } from '../waveform';
import type { ComputedConstraints } from './constraints';
import type { BlackmanWaveform } from '~/models/studio';
import type { Vec2 } from '~/util';

export const wfBlackman: WaveformApi<BlackmanWaveform> = {
  samples: blackmanSamples,
  sampleAt: defaultSampleAt,
  bounds: blackmanBounds,
  points: blackmanPoints,
  duration: blackmanDuration,
  area: blackmanArea
};

function blackmanDuration(wf: BlackmanWaveform): number {
  if (wf.constraint.kind === 'duration') {
    return getBlackmanExactConstraints(wf).duration;
  } else {
    return wf.constraint.duration;
  }
}

function blackmanArea(wf: BlackmanWaveform): number {
  if (wf.constraint.kind === 'area') {
    return getBlackmanExactConstraints(wf).area;
  } else {
    return wf.constraint.area;
  }
}

function blackmanNormSamples(duration: number): number[] {
  if (duration === 0) {
    return [];
  }
  if (duration === 1) {
    return [1];
  }
  const blackmanSamples: number[] = [];
  for (let n = 1 - duration; n < duration; n += 2) {
    const blackman =
      0.42 +
      0.5 * Math.cos((Math.PI * n) / (duration - 1)) +
      0.08 * Math.cos((2.0 * Math.PI * n) / (duration - 1));
    blackmanSamples.push(blackman);
  }
  return blackmanSamples;
}

function getBlackmanExactConstraints(
  wf: BlackmanWaveform
): ComputedConstraints {
  const fromCache = constraintCache.get(wf);
  if (fromCache != null) {
    return fromCache;
  }
  const computed = computeBlackmanExactConstraints(wf);
  constraintCache.set(wf, computed);
  return computed;
}

const MAX_EXACT_DURATION = 2000;
const blackmanSumCache: Float64Array = new Float64Array(MAX_EXACT_DURATION);

function blackmanSumForDuration(duration: number): number {
  assert(Number.isInteger(duration));
  if (duration <= 1) {
    return duration;
  }
  let sum = 0.42 * duration;
  // for sufficiently large durations, the first order estimate is close enough to the exact value for our purposes
  if (duration >= MAX_EXACT_DURATION) {
    return sum;
  }

  // for smaller durations, we calculate the exact value from samples and cache it
  const fromCache = blackmanSumCache[duration];
  if (fromCache != 0) {
    return fromCache;
  }

  for (let n = 1 - duration; n < duration; n += 2) {
    const blackman =
      0.5 * Math.cos((Math.PI * n) / (duration - 1)) +
      0.08 * Math.cos((2.0 * Math.PI * n) / (duration - 1));
    sum += blackman;
  }

  blackmanSumCache[duration] = sum;
  return sum;
}

function computeBlackmanExactConstraints(
  wf: BlackmanWaveform
): ComputedConstraints {
  switch (wf.constraint.kind) {
    case 'maxVal': {
      const { duration, area } = wf.constraint;
      if (duration <= 0) {
        return {
          duration: 0,
          maxVal: 0,
          area: 0
        };
      }
      const sum = blackmanSumForDuration(duration);
      const maxVal = sum > 0 ? (wf.constraint.area / sum) * 1000 : 0;
      return { duration, maxVal, area };
    }
    case 'area': {
      const { duration, maxVal } = wf.constraint;
      if (duration <= 0) {
        return {
          duration: 0,
          maxVal: 0,
          area: 0
        };
      }
      const sum = blackmanSumForDuration(duration);
      const area = (sum * maxVal) / 1000;
      return { duration, maxVal, area };
    }
    case 'duration': {
      const { maxVal, area } = wf.constraint;
      if (maxVal <= 0 || area <= 0) {
        return {
          duration: 0,
          maxVal: 0,
          area: 0
        };
      }
      let duration = Math.ceil((area / (0.42 * maxVal)) * 1000);
      // check actual maxVal, increase duration if needed to ensure maxVal constraint is not exceeded
      for (;;) {
        const sum = blackmanSumForDuration(duration);
        const actualMaxVal = (area / sum) * 1000;
        if (actualMaxVal > maxVal) {
          duration++;
        } else {
          return {
            duration,
            maxVal,
            area
          };
        }
      }
    }
    default:
      return assertNever(wf.constraint);
  }
}

function blackmanPoints(wf: BlackmanWaveform, xOffset: number): Vec2[] {
  const { duration, maxVal } = getBlackmanExactConstraints(wf);
  const norm = blackmanNormSamples(duration);
  return norm.map((sample, i) => ({ x: xOffset + i, y: sample * maxVal }));
}

function blackmanSamples(wf: BlackmanWaveform) {
  const { duration, maxVal } = getBlackmanExactConstraints(wf);
  const norm = blackmanNormSamples(duration);
  return norm.map((n) => n * maxVal);
}

function blackmanBounds(wf: BlackmanWaveform) {
  switch (wf.constraint.kind) {
    case 'maxVal':
      return {
        min: 0,
        max: getBlackmanExactConstraints(wf).maxVal
      };
    case 'duration':
    case 'area':
      return { min: 0, max: wf.constraint.maxVal };
    default:
      assertNever(wf.constraint);
  }
}
